import {
  Avatar,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { useFieldArray, UseFormSetValue } from 'react-hook-form'
import {
  DateTimePickerField,
  Icons,
  TextInputField,
  TextInputFieldProps,
  ToggleInputField,
} from 'utoniq-ui'

import {
  ArtistBannerDetailFragment,
  ArtistUpdateArtistBannerInput,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { FileUploadInfo } from '@/components/pages/ConsoleCollectionCreate/template/CollectionCreateForm'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import {
  validateBannerDateOrder,
  validatePublicBannerEndDateRequired,
  validateReservationBannerEndDate,
  validateReservationBannerOverlap,
} from '@/core/validators'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

export type ConsoleCampaignReservationBannerSettingFormProps = {
  loading: boolean
  bannerSettings: Maybe<ArtistBannerDetailFragment[]>
  onSubmit: (
    inputs: ReservationSubmitValues,
    isReservationBanners?: boolean
  ) => Promise<void>
  fileUploadInfo: FileUploadInfo
  isReservationBanners?: boolean
  publicBannerStartedAt?: string
  publicBannerEndedAt?: string
}

export type ReservationSubmitValues = {
  banners: ArtistUpdateArtistBannerInput[]
  bannersStartedAt: string
  bannersEndedAt: string
  bannersStatus: boolean
  publicBannerStartedAt?: string
  publicBannerEndedAt?: string
}

const schema: UseFormProps['schema'] = o => ({
  banners: o.array().items(
    o.object({
      tmpPath: o.string().allow('').optional(),
      link: o.object().when('tmpPath', {
        is: '',
        then: o
          .object({
            externalUrl: o.string().optional().allow(''),
            innerPath: o.string().optional().allow(''),
          })
          .optional(),
        otherwise: o.object({
          externalUrl: o
            .string()
            .label(tLabel('consoleCampaignBannerSetting.link.externalUrl')),
          innerPath: o.string().optional().allow(''),
        }),
      }),
    })
  ),
  bannersStartedAt: o
    .date()
    .allow('')
    .optional()
    .custom((value: Date, helpers) => {
      if (!value) {
        return value
      }
      const [data] = helpers?.state?.ancestors || []

      // 開始日・終了日の順序チェック
      const dateOrderResult = validateBannerDateOrder(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'bannersEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.invalidDateOrder'
      )
      if (dateOrderResult !== value) {
        return dateOrderResult
      }

      // 公開バナーの終了日時が設定されているかチェック
      const endDateRequiredResult = validatePublicBannerEndDateRequired(
        value,
        helpers,
        data,
        'publicBannerStartedAt',
        'publicBannerEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.publicEndDateRequired'
      )
      if (endDateRequiredResult !== value) {
        return endDateRequiredResult
      }

      // 公開バナーとの重複チェック
      const overlapResult = validateReservationBannerOverlap(
        value,
        helpers,
        data,
        'publicBannerStartedAt',
        'publicBannerEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.overlapPublic'
      )
      if (overlapResult !== value) {
        return overlapResult
      }

      // 公開バナーとの前後関係チェック
      const orderResult = validateReservationBannerEndDate(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'publicBannerStartedAt',
        'publicBannerEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.overlapPublic',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.reservationAfterPublic'
      )
      if (orderResult !== value) {
        return orderResult
      }

      return value
    })
    .label(tLabel('consoleCampaignBannerSetting.startedAt')),
  bannersEndedAt: o
    .date()
    .allow('')
    .optional()
    .custom((value: Date, helpers) => {
      if (!value) {
        return value
      }
      const [data] = helpers?.state?.ancestors || []

      // 開始日・終了日の順序チェック
      const dateOrderResult = validateBannerDateOrder(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'bannersEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.invalidDateOrder'
      )
      if (dateOrderResult !== value) {
        return dateOrderResult
      }

      // 公開バナーの終了日時が設定されているかチェック
      const endDateRequiredResult = validatePublicBannerEndDateRequired(
        value,
        helpers,
        data,
        'publicBannerStartedAt',
        'publicBannerEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.publicEndDateRequired'
      )
      if (endDateRequiredResult !== value) {
        return endDateRequiredResult
      }

      // 公開バナーとの重複チェック
      const overlapResult = validateReservationBannerOverlap(
        value,
        helpers,
        data,
        'publicBannerStartedAt',
        'publicBannerEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.overlapPublic'
      )
      if (overlapResult !== value) {
        return overlapResult
      }

      // 公開バナーとの前後関係チェック
      const endDateResult = validateReservationBannerEndDate(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'publicBannerStartedAt',
        'publicBannerEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.overlapPublic',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.reservationAfterPublic'
      )
      if (endDateResult !== value) {
        return endDateResult
      }

      return value
    })
    .label(tLabel('consoleCampaignBannerSetting.endedAt')),
  bannersStatus: o.boolean().allow(false).optional(),
  publicBannerStartedAt: o.date().allow(null, '').optional(),
  publicBannerEndedAt: o.date().allow(null, '').optional(),
})

export const ConsoleCampaignReservationBannerSettingForm: Organism<ConsoleCampaignReservationBannerSettingFormProps> =
  ({
    loading,
    bannerSettings,
    onSubmit,
    fileUploadInfo,
    isReservationBanners,
    publicBannerStartedAt,
    publicBannerEndedAt,
  }) => {
    const { t } = useTranslation('consoleCampaignBannerSetting')
    const { showMessage, showError } = useMessage()
    const {
      handleSubmit,
      register,
      setValue,
      reset,
      setError,
      control,
      watch,
      formState: { errors, isSubmitting },
    } = useForm<ReservationSubmitValues>({
      schema,
      defaultValues: {
        ...defaultValues,
      },
    })

    const { fields } = useFieldArray({ control, name: 'banners' })

    useEffect(() => {
      // isReservationBannersが変更された時にフォームをリセット
      reset(defaultValues)
    }, [isReservationBanners, reset])

    useEffect(() => {
      // 公開バナーの値を設定
      if (publicBannerStartedAt || publicBannerEndedAt) {
        setValue('publicBannerStartedAt', publicBannerStartedAt || '')
        setValue('publicBannerEndedAt', publicBannerEndedAt || '')
      }

      if (!bannerSettings?.length) {
        reset({
          ...defaultValues,
          publicBannerStartedAt: publicBannerStartedAt || '',
          publicBannerEndedAt: publicBannerEndedAt || '',
        })
        return
      }
      reset({
        banners: defaultValues.banners?.map((_, index) => ({
          link: {
            externalUrl:
              bannerSettings[index]?.link?.externalUrl ??
              bannerSettings[index]?.link?.innerPath ??
              '',
            innerPath: '',
          },
          tmpPath: bannerSettings[index]?.thumbnail.uri ?? '',
        })),
        bannersStartedAt: bannerSettings[0]?.bannersStartedAt ?? '',
        bannersEndedAt: bannerSettings[0]?.bannersEndedAt ?? '',
        bannersStatus: bannerSettings[0]?.bannerStatus === 'published' || false,
        publicBannerStartedAt: publicBannerStartedAt ?? '',
        publicBannerEndedAt: publicBannerEndedAt ?? '',
      })
    }, [
      bannerSettings,
      reset,
      publicBannerStartedAt,
      publicBannerEndedAt,
      setValue,
    ])

    const handleOnSubmit = handleSubmit((values: ReservationSubmitValues) => {
      return onSubmit(values, isReservationBanners)
        .then(() => {
          showMessage(t('registerBannerSuccess'), 'success')
        })
        .catch(e => {
          handleError<ReservationSubmitValues>(e, setError, showError)
        })
    })

    return (
      <Form onSubmit={handleOnSubmit}>
        <Stack>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              mb: 2,
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {t('addedBannerImage', 'バナー画像を追加')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t(
              'campaignBannerSettingDesc',
              'サービストップ画面に表示するバナーを最大3つまで登録することができます。'
            )}
          </Typography>
        </Stack>
        {loading ? (
          <Stack spacing={5}>
            {defaultValues.banners?.map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                sx={{ borderRadius: 2 }}
                width="100%"
                height={124}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={5}>
            {fields.map((field, index) => (
              <CampaignBannerCell
                key={field.id}
                index={index}
                thumbnailUploadInfo={fileUploadInfo}
                setValue={setValue}
                defaultValue={bannerSettings?.[index]?.thumbnail?.uri ?? ''}
                inputProps={{
                  ...register(`banners.${index}.link.externalUrl`),
                  error: errors.banners?.[index]?.link?.externalUrl,
                  type: 'text',
                }}
              />
            ))}
            <ToggleInputField
              checked={watch('bannersStatus')}
              onChange={() => {
                const currentValue = watch('bannersStatus')
                setValue('bannersStatus', !currentValue, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
              content={{
                title: t('bannerStatus'),
                description: (
                  <Typography
                    sx={{
                      width: 335,
                      height: 60,
                      fontSize: 13,
                      color: 'text.secondary',
                    }}
                  >
                    {t('bannerStatusDesc')}
                  </Typography>
                ),
              }}
              sx={{
                px: 0,
                pt: 0,
                '&& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
                  bgcolor: 'primary.main',
                },
              }}
            />
            <Stack spacing={3}>
              <DateTimePickerField
                onChange={date => {
                  setValue(
                    'bannersStartedAt',
                    (convertDatePickerValueToDate(date) || '') as never,
                    { shouldValidate: true, shouldDirty: true }
                  )
                }}
                value={
                  watch('bannersStartedAt')
                    ? dayjs(watch('bannersStartedAt'))
                    : null
                }
                title={t('startedAt')}
                description={t('startedAtDesc')}
                placeholder={t('startedAtPlaceholder')}
                error={errors?.bannersStartedAt}
                titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
              />
              <DateTimePickerField
                onChange={date => {
                  setValue(
                    'bannersEndedAt',
                    (convertDatePickerValueToDate(date) || '') as never,
                    { shouldValidate: true, shouldDirty: true }
                  )
                }}
                value={
                  watch('bannersEndedAt')
                    ? dayjs(watch('bannersEndedAt'))
                    : null
                }
                title={t('endedAt')}
                description={t('endedAtDesc')}
                placeholder={t('endedAtPlaceholder')}
                error={errors?.bannersEndedAt}
                titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
              />
            </Stack>
          </Stack>
        )}
        <CellFrame sx={{ py: 5, textAlign: 'center' }}>
          <Button
            type="submit"
            size="large"
            sx={{ width: 244 }}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('submit', '登録する')}
          </Button>
        </CellFrame>
      </Form>
    )
  }

type CampaignBannerCellProps = {
  index: number
  defaultValue: string
  inputProps: Omit<TextInputFieldProps, 't'>
  thumbnailUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<ReservationSubmitValues>
}

const CampaignBannerCell = ({
  index,
  defaultValue,
  inputProps,
  thumbnailUploadInfo,
  setValue,
}: CampaignBannerCellProps) => {
  const { t } = useTranslation('consoleBusinessEdit')
  const [imageUrl, setImageUrl] = useState<string>('')

  useEffect(() => {
    setImageUrl(defaultValue)
  }, [defaultValue])

  const handleUploadFile = useCallback(async () => {
    const result = await thumbnailUploadInfo.onDrop()
    if (!result) return
    setImageUrl(result.imageUrl ?? '')
    setValue(`banners.${index}.tmpPath`, result.imageUploadPath ?? '', {
      shouldValidate: true,
    })
  }, [thumbnailUploadInfo, setValue, index])

  const handleRemoveUploadFile = useCallback(() => {
    setValue(`banners.${index}.tmpPath`, '')
    setValue(`banners.${index}.link.externalUrl`, '')
    setImageUrl('')
  }, [setValue, index])

  return (
    <CellFrame sx={{ display: 'flex', justifyContent: 'center' }}>
      <Card
        elevation={0}
        sx={{
          borderWidth: 1,
          borderColor: 'grey.100',
          borderStyle: 'dashed',
          bgcolor: 'transparent',
          borderRadius: 2,
          width: imageUrl ? '100%' : 'auto',
        }}
      >
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            {!!imageUrl && (
              <Button
                variant="text"
                startIcon={<Icons.Trash color="error" />}
                color="error"
                onClick={handleRemoveUploadFile}
              >
                {t('delete', '削除する')}
              </Button>
            )}
          </Stack>
          <Stack sx={{ pt: 2 }}>
            {imageUrl ? (
              <Avatar
                variant="square"
                src={imageUrl}
                sx={{ height: 124, width: '100%' }}
              />
            ) : (
              <Stack spacing={2} alignItems="center">
                <Icons.Drop
                  sx={{
                    width: 56,
                    height: 46,
                    color: 'grey.400',
                  }}
                />
                <Typography variant="caption" color="text.hint">
                  {t(
                    'addedBannerImageDesc',
                    '幅750px 高さ315px / PNG・JPEG・HEICファイル'
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ width: 'fit-content' }}
                  onClick={handleUploadFile}
                >
                  {t('selectFiles', 'ファイルを選択')}
                </Button>
              </Stack>
            )}
          </Stack>
          {!!imageUrl && (
            <Stack sx={{ pt: 5 }}>
              <TextInputCell {...inputProps} />
            </Stack>
          )}
        </CardContent>
      </Card>
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  ({ titleProps, error, ...props }: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleBusinessEdit')
    return (
      <CellFrame>
        <TextInputField
          {...props}
          titleProps={{
            variant: 'h6',
            color: 'text.primary',
            children: t('linkURL', 'リンク先のURL'),
          }}
          textFieldProps={{
            sx: { mb: 0, fieldset: { border: 'none' }, input: { p: 0 } },
            helperText: error?.message,
            placeholder: t(
              'inputLinkURLPlaceholder',
              '入力してください (例 https://xxxxxx)'
            ),
          }}
          error={error}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

const defaultValues: ReservationSubmitValues = {
  banners: [
    {
      link: {
        externalUrl: '',
        innerPath: '',
      },
      tmpPath: '',
    },
    {
      link: {
        externalUrl: '',
        innerPath: '',
      },
      tmpPath: '',
    },
    {
      link: {
        externalUrl: '',
        innerPath: '',
      },
      tmpPath: '',
    },
  ],
  bannersStartedAt: '',
  bannersEndedAt: '',
  bannersStatus: false,
  publicBannerStartedAt: '',
  publicBannerEndedAt: '',
}
